import React, { useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router";
import Button from "react-bootstrap/Button";
import { duplicateQuote, checkQuoteName } from "store/priceQuote/actionCreator";
import Modal from "common/modal";
import InputField from "common/customInputField";
import { useDispatch, useSelector } from "react-redux";
import { api } from "store/slices/api";
import { debounce, isEmpty } from "lodash";
import { toast } from "react-toastify";

const DuplicateQuoteModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { quoteId } = useParams();
  const { sub } = useSelector((state) => state.loggedInUser);

  const [show, setShow] = useState(false);
  const [isShowConfirmDuplicateQuote, setIsShowConfirmDuplicateQuote] = useState(false);
  const [storeData, setStoreData] = useState("");
  const [duplicateData, setDuplicateData] = useState({ quoteName: null, storeNumber: null, territory: null });
  const [refreshPrices, setRefreshPrices] = useState(true);
  const [quoteNameAlreadyExists, setQuoteNameAlreadyExists] = useState(true);
  const [waitingForNameCheck, setWaitingForNameCheck] = useState(false);
  const checkIfNameAlreadyExists = useCallback(
    debounce((name) => {
      dispatch(checkQuoteName(name)).then(({ status }) => setQuoteNameAlreadyExists(status === "0"));
      setWaitingForNameCheck(false);
    }, 1000),
    [],
  );

  const { data: quote = {} } = api.useGetPriceQuoteQuery(quoteId);
  const [fetchStoreInfo] = api.useLazyGetStoreInfoQuery();

  const storeName = storeData.storeName ?? quote.storeName;
  const storePhoneNumber = storeData.storePhoneNumber ?? quote.phone;
  const quoteName = duplicateData.quoteName ?? quote.quoteName;
  const storeNumber = duplicateData.storeNumber ?? quote.storeNumber;
  const territory = duplicateData.territory ?? quote.territory;

  const confirmDuplicateQuote = async () => {
    let payload = { quoteId, user: sub, storeName, storePhoneNumber, quoteName, storeNumber, territory, refreshPrices };
    const res = await toast.promise(dispatch(duplicateQuote(payload)), {
      pending: "Duplicating quote",
      success: "Successfully duplicated quote",
      error: "Something went wrong",
    });
    res?.quoteData?.quoteId && navigate(`/priceQuote/${res.quoteData.quoteId}#quote-summary`);
    setShow(false);
  };

  return (
    <>
      <Button variant="outline-primary" onClick={() => setShow(true)}>
        Duplicate Quote
      </Button>
      {show && (
        <Modal
          title="Duplicate Quote"
          handleSave={() => (quoteNameAlreadyExists ? setIsShowConfirmDuplicateQuote(true) : confirmDuplicateQuote())}
          handleCancel={() => setShow(false)}
          size="lg"
          sectionName=""
          closeButtonText="Cancel"
          saveButtonText="Duplicate"
          isSaveBtnDisabled={waitingForNameCheck}
        >
          <div className="container-fluid p-2 border-0 m-0">
            <div className="search-form-container__fields">
              <form className="search-form-container__fields__form">
                <div className="row align-items-center">
                  <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                    <InputField
                      type="text"
                      id="quoteName"
                      label="Quote Name"
                      placeholder="Enter Quote Name"
                      fieldClasses="form-control search-form-container__fields__form-group__item"
                      value={quoteName ?? ""}
                      onChange={(e) => {
                        const quoteName = e.target.value;
                        setWaitingForNameCheck(true);
                        checkIfNameAlreadyExists(quoteName);
                        return setDuplicateData({ ...duplicateData, quoteName });
                      }}
                      disabled={false}
                      maxLength={40}
                      errorMsg={isEmpty(quoteName) ? "Please enter Quote Name" : ""}
                    />
                  </div>
                </div>
                <div className="row align-items-center">
                  <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                    <InputField
                      type="text"
                      id="storeNumber"
                      label="Store Number"
                      placeholder="Enter Store Number"
                      fieldClasses="form-control search-form-container__fields__form-group__item"
                      value={storeNumber ?? ""}
                      onChange={(e) => {
                        const { id, value } = e.target;
                        setDuplicateData({ ...duplicateData, storeNumber: value });
                        fetchStoreInfo({ storeNumber: value }).then((res) => {
                          setStoreData((storeData) => res.data?.[0] ?? storeData);
                        });
                      }}
                      isRequired={true}
                      errorMsg={isEmpty(storeNumber) ? "Please enter a store number" : ""}
                    />
                  </div>
                  <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                    <InputField
                      type="text"
                      id="territory"
                      label="Territory Number"
                      placeholder="Enter Territory Number"
                      fieldClasses="form-control search-form-container__fields__form-group__item"
                      value={territory ?? ""}
                      onChange={(e) => setDuplicateData({ ...duplicateData, territory: e.target.value })}
                      isRequired={true}
                      errorMsg={isEmpty(territory) ? "Please enter a territory" : ""}
                    />
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <label className="switch mb-0">
                    <input
                      type="checkbox"
                      id="refresh-prices-toggle"
                      checked={refreshPrices}
                      onChange={() => setRefreshPrices(!refreshPrices)}
                    />
                    <span className="slider round" />
                  </label>
                  <span style={{ paddingLeft: 10 }}>Refresh product and labor prices</span>
                </div>
              </form>
            </div>
          </div>
        </Modal>
      )}
      {isShowConfirmDuplicateQuote && (
        <Modal
          title="Are you Sure?"
          handleSave={() => {
            setIsShowConfirmDuplicateQuote(false);
            confirmDuplicateQuote();
          }}
          handleCancel={() => setIsShowConfirmDuplicateQuote(false)}
          size="lg"
          sectionName=""
          closeButtonText="Cancel"
          saveButtonText="Continue"
        >
          <div className="container-fluid p-2 border-0 m-0">
            <p>
              There is already a quote named <b>{duplicateData.quoteName ?? quote.quoteName}</b>. Do you want to
              continue with the same name?
            </p>
          </div>
        </Modal>
      )}
    </>
  );
};

export default DuplicateQuoteModal;
